import {
    AfterContentInit,
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { PageInfo } from '../../../../core/model/pagination/page-info';
import { PaginatorParams } from '../../../../core/model/pagination/paginator-params';
import { PaginableItemAdapter } from './paginable-item-adapter';
import { PaginatorComponent } from '../paginator/paginator.component';
import { PaginableResponse } from '../../../../core/model/pagination/paginable-response.model';

/**
 * Presentational component. Displays list of items and paginator - accepts observable of items.
 *
 * The component is adapts passed data using input parameter adapter.
 *
 * Emits pageChanges.
 */
@Component({
    selector: 'ssf-paginable-list',
    template: `<div>
        <div #container></div>

        <div #containerPaginator></div>
        <!-- Cannot use the paginable component in template, see comment in createPaginatorComponent() method bellow
             <div *ngIf="paginatorParams">
             <ssf-paginator [state]="paginatorParams" (pageChanged)="onPageChanged($event)"></ssf-paginator>
         </div>-->

        <div *ngIf="showNoDataMessage" class="ssf-no-data-info">
            {{adapter.getNoDataTranslationPath() | translate}}
        </div>

    </div>`
})
export class PaginableListComponent<T, M extends PaginableResponse, C> implements AfterContentInit, OnDestroy {

    @Input() public adapter: PaginableItemAdapter<T, M, C>;
    @Input() list: Observable<M>;
    @Input() pageSize: number;
    private subscription: Subscription;
    private paginatorParams;

    @Output() loadPage = new EventEmitter();
    @ViewChild('container', {read: ViewContainerRef}) container;
    @ViewChild('containerPaginator', {read: ViewContainerRef}) containerPaginator;

    public itemComponents: ComponentRef<C>[] = [];
    private paginatorComponent: ComponentRef<PaginatorComponent>;
    public showNoDataMessage = false;

    constructor(private resolver: ComponentFactoryResolver) {}

    ngAfterContentInit(): void {
        this.subscription = this.list.subscribe(this.modelChanged.bind(this));
    }

    modelChanged(list: M) {
        if (list) {
            this.initPaginatorComponentParams(list);

            this.itemComponents = this.destroyComponents(this.itemComponents);
            this.itemComponents = this.createComponents(list);

            this.paginatorComponent = this.destroyPaginator(this.paginatorComponent);
            this.paginatorComponent = this.createPaginatorComponent(list);
        }

        this.showNoDataMessage = this.getShowNoDataMessage(list);
    }

    destroyComponents(itemComponents: ComponentRef<C>[]): ComponentRef<C>[] {
        itemComponents.forEach(ref => ref.destroy());
        return [];
    }

    createComponents(list): ComponentRef<C>[] {
        if (!list) {
            return [];
        }

        const result: ComponentRef<C>[] = [];
        const self = this;
        const items = this.adapter.getItems(list);

        if (items) {
            items.forEach(item => {
                let componentRef = self.container.createComponent(this.resolver.resolveComponentFactory(this.adapter.getType()));
                this.adapter.setProperties(componentRef, item);
                result.push(componentRef);
            });

            return result;
        }
    }

    /**
     * Determines if no data message should be shown - only if there is length present in object - it means that GET has been
     * finished and there are no items in the response.
     * @param list returned list
     * @return {boolean} if no data message should be displayed
     */
    public getShowNoDataMessage(list): boolean {
        if (!list) {
            return false;
        }

        const items = this.adapter.getItems(list);

        if (items  &&  items.length === 0) {
            return true;
        }
    }

    /**
     * This function creates a new instance of `paginatorComponent` instead of using
     * <ssf-paginator [state]="paginatorParams" ...> in template.
     * Reason:
     * Pagination data (page, itemsPerPage) is passed to ngx bootstrap `pagination` component.
     * For example: SSF: AddApplicationListComponent(addApplicationList$) ->
     *                  SSF: PaginableListComponent(paginatorParams) ->
     *                      SSF: PaginatorComponent(page, itemsPerPage, totalItems) ->
     *                          NGX Bootstrap: PaginationComponent
     *
     * When pagination data is changed from above - for example some parent component subscribed to store changes the
     * pagination data, the NGX PaginationComponent emits an event that page has been changed resulting in another backend
     * fetch.
     * Unfortunately there is no way how to distinguish mouse click event from event emitted by PaginationCOmponent as
     * a result of pagination data change.
     * The component is destroyed and recreated every time the pagination changes.
     *
     * Example:
     * A user wants to add application. He changes the search text and click search. There i a server fetch with page = 0,
     * itemsPerPage = 4, searchText=blabla. A list of applications matching the filter is returned and it has for
     * example 10 pages. User navigates to page no. 5. Then he decides to change text filter - changes
     * text filter anc clicks Search button. A new list is returned. It is a new search with a new search term and thus the page
     * no. 0 is required. The server returns page 0, and for example count of pages 3.
     * The Ngx PaginationComponent holds data: page = 5. Parent changes pagination data to page = 0, count = 3.
     * PaginationComponent detects that data has been changed and emits an event with page = 3 - current max page.
     * This results in another server fetch (and I do not want this fetch!!!)
     *
     * Removing and recreating fixes this issue. This is certainly not optimal solution.
     */
    createPaginatorComponent(list) {
        if (!list  ||  !this.itemComponents.length) {
            return;
        }

        let componentRef = this.containerPaginator.createComponent(this.resolver.resolveComponentFactory(PaginatorComponent));
        componentRef.instance.state = this.paginatorParams;
        componentRef.instance.pageChanged.subscribe(event => this.onPageChanged(event));

        return componentRef;
    }

    destroyPaginator(component: ComponentRef<PaginatorComponent>) {
        if (component) {
            component.destroy();
        }

        return null;
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }

        this.destroyComponents(this.itemComponents);
        this.destroyPaginator(this.paginatorComponent);
    }

    onPageChanged(pageInfo: PageInfo): void {
        let newPageInfo = new PageInfo(pageInfo.page - 1, this.pageSize);
        this.loadPage.emit(newPageInfo);
    }

    initPaginatorComponentParams(list: M) {
        if (list) {
            this.paginatorParams = new PaginatorParams().withPage(list.pageNumber + 1, this.pageSize);
            this.paginatorParams.totalItems = list.pageCount * this.pageSize;
        }
    }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/modules/shared/components/paginable-list/paginable-list.component.ts